import {LitElement, css, html} from "lit";
import {LANG_DEF} from "./profile-finance-grid-balance-sheet.js";
import {totalDCF, totalMultiples, totalVC, totalMarket} from "./profile-finance-valuation.js";
import {MAX_YEAR, MIN_YEAR} from "./profile-finance-grid.js";

export class ProfileFinanceValuationChart extends LitElement {

	static get properties() {
		return {
			lang: { type: String, reflect: true },
			profileID: { type: String, attribute: "profile-id", reflect: true },
			loading: { type: Boolean, attribute: false },
			error: { type: String, attribute: false },
			year: { type: Number, attribute: false },
		};
	}

	get ChartContainer() {
		if(!this._chartContainer)
			this._chartContainer = this.shadowRoot.querySelector("#ChartContainer");
		return this._chartContainer;
	}

	constructor() {
		super();

		this.lang = "en";

		/**
		 * Next rows loading indicator. (Not for changing year view)
		 *
		 * @type {boolean}
		 */
		this.loading = false;

		/**
		 * The error message when something went wrong and the grid can no longer show valid data.
		 *
		 * @type {string|null}
		 */
		this.error = null;

		/**
		 * The initial year.
		 *
		 * The columns and rows render depending on this value.
		 *
		 * The columns are composed out of this value: (year - 2) & (year) & (year + 2) which are 5 years in total, with
		 * the current year in the center.
		 *
		 * @type {number}
		 */
		this.year = new Date().getFullYear();

		this.chart = null;
	}

	attributeChangedCallback(name, old, value) {
		super.attributeChangedCallback(name, old, value);

		switch(name) {
			case "profile-id":
				if(old !== undefined)
					this.reset();
				if(value)
					requestAnimationFrame(() => this.refresh());
				break;
		}
	}

	reset() {
		this.loading = false;
		this.error = null;
		this.year = new Date().getFullYear();
	}

	refresh() {
		this.load(this.year);
	}

	/**
	 * Changes the view of the years by adding 1.
	 */
	nextYear() {
		if(this.year+1 > MAX_YEAR)
			return;

		this.year++;
		this.refresh();
	}

	/**
	 * Changes the view of the years by subtracting 1.
	 */
	prevYear() {
		if(this.year-1 < MIN_YEAR)
			return;

		this.year--;
		this.refresh();
	}

	/**
	 * Loads the table data.
	 *
	 * The next rows will be loaded when fromYear and toYear is provided.
	 *
	 * Only the data for the column year is loaded when only fromYear is provided.
	 */
	load(fromYear, toYear = null) {
		this.error = null;

		if(!this.profileID) {
			this.error = "No Profile ID defined. " + this.profileID;
			return;
		}

		const query = `
			fragment FinanceValuation on Finance {
				# DCF Data
				PNLValues(fromYear: $fromYear, toYear: $toYear, filter: { Type: FREE_CASH_FLOW }) {
					Year
					Value {Amount}
				}
				
				Valuation(fromYear: $fromYear) {
					# DCF
					WAAC
					LongTermGrowth
					
					# Multiples
					RevenueMultiple
					EBITMultiple
					RevenueValue
					EBITValue
					
					# VC
					Investment {Amount}
					Horizon
					HorizonRevenueValue
				    HorizonEBITValue
					ReturnMultiple
				}
			}
	
			fragment FinanceValuationMarkets on Finance {
				ValuationMarkets {
					ID
					CompanyName
					Value
					Factor
				}
			}
			
			query ProfileFinanceValuation($profileID: ID!, $fromYear: Int!, $toYear: Int = null, $withMarkets: Boolean!) {
				getProfile(id: $profileID) {
					... on CompanyProfile {
						Finance {
							...FinanceValuation
							...FinanceValuationMarkets @include(if: $withMarkets)
						}
					}
					... on ShareholderProfile {
						Finance {
							...FinanceValuation
							...FinanceValuationMarkets @include(if: $withMarkets)
						}
					}
				}
			}
		`;

		const variables = {
			profileID: this.profileID,
			fromYear: fromYear,
			toYear: fromYear + 4,
			withMarkets: true
		};

		this.loading = true;

		fetch("/graphql", {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				"Accept": "application/json"
			},
			body: JSON.stringify({
				query: query,
				variables: variables
			})
		}).then(r => r.json())
		.then(response => {
			if(!response || response.errors) {
				this.error = "Data Error:" + JSON.stringify(response);
				return;
			}

			const chartData = [
				null, // DCF
				null, // Multiple
				null, // VC
				null, // Market
			];

			if(response.data.getProfile && response.data.getProfile.Finance) {
				const finance = response.data.getProfile.Finance;

				if(finance.PNLValues) {
					// Holds all merged Free Cash Flow values
					const freeCashFlow = {};

					finance.PNLValues.forEach(pnlValue => {
						if(pnlValue.Value) {
							if(!freeCashFlow.hasOwnProperty(pnlValue.Year))
								freeCashFlow[pnlValue.Year] = 0;
							freeCashFlow[pnlValue.Year] += parseFloat(pnlValue.Value.Amount);
						}
					});

					// Adds the values if not 0
					const finalFreeCashFlow = {};
					for(let year in freeCashFlow) {
						if(freeCashFlow[year])
							finalFreeCashFlow[year] = freeCashFlow[year];
					}

					chartData[0] = totalDCF(fromYear, finance.Valuation, finalFreeCashFlow);
				}

				if(response.data.getProfile.Finance.Valuation) {
					chartData[1] = totalMultiples(finance.Valuation);
					chartData[2] = totalVC(finance.Valuation);
				}

				if(finance.ValuationMarkets.length > 0)
					chartData[3] = totalMarket(finance.ValuationMarkets);
			}

			if(this.chart !== null)
				this.updateChart(chartData);
			else
				this.buildChart(chartData);
		})
		.catch(reason => this.error = reason)
		.finally(() => this.loading = false);
	}

	updateChart(data) {
		this.chart.series[0].setData(data);
	}

	buildChart(data) {
		this.chart = Highcharts.chart(this.ChartContainer, {
			chart: {
				type: "bar",
				height: 130
			},

			title: {
				text: ""
			},

			subtitle: {
				text: ""
			},

			yAxis: {
				title: {
					text: ''
				}
			},

			xAxis: {
				categories: ["DCF", "Multiples", "VC", "Market"],
			},

			legend: {
				enabled: false
			},

			credits: {
				enabled: false
			},

			plotOptions: {
				series: {
					stacking: "normal"
				}
			},

			tooltip: {
				pointFormat: '<b>{point.y}</b>'
			},

			colors: [
				"var(--aumentoo-highcharts-color0)",
			],

			series: [{
				data: data
			}],
		});
	}

	static get styles() {
		return css`
			:host {
				display: block;
			}
			
			.container {
				border-radius: 8px;
			}
		`;
	}

	render() {
		return html`<div id="ChartContainer" class="container"></div>`;
	}

}

customElements.define("profile-finance-valuation-chart", ProfileFinanceValuationChart);